import React, { useEffect, useRef, useState } from "react";
import { Paperclip, Upload, Trash2, Download, Loader2, FileText, Image as ImageIcon, Film } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "public",
  "x-user-role": session?.user?.role || "client",
});

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fileIcon = (ct = "") => {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  return FileText;
};

/**
 * Unified file uploader + list.
 *
 * Props:
 *   session, context ("assessment_response" | "tenant_asset" | "prospect"),
 *   contextId (optional), label (shown above list), emptyHint, compact (bool),
 *   accept (input accept attribute), showLabelInput (bool) — when true, prompts
 *   user to name the asset before upload.
 */
export default function FileAttachments({
  session,
  context,
  contextId,
  label = "Attachments",
  emptyHint = "No files attached yet",
  compact = false,
  accept = "*/*",
  showLabelInput = false,
  dataTestidPrefix = "file",
}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [labelText, setLabelText] = useState("");
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const params = new URLSearchParams({ context });
      if (contextId) params.set("context_id", contextId);
      const r = await fetch(`${BACKEND}/api/files/list?${params}`, { headers: authHeaders(session) });
      const data = await r.json();
      setFiles(Array.isArray(data) ? data : []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, contextId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("context", context);
      if (contextId) form.append("context_id", contextId);
      if (labelText) form.append("label", labelText);
      const res = await fetch(`${BACKEND}/api/files/upload`, {
        method: "POST",
        headers: authHeaders(session),
        body: form,
      });
      if (!res.ok) throw new Error((await res.json())?.detail || "Upload failed");
      toast.success("File uploaded");
      setLabelText("");
      refresh();
    } catch (err) {
      toast.error("Upload failed", { description: err?.message });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      const res = await fetch(`${BACKEND}/api/files/${id}`, {
        method: "DELETE",
        headers: authHeaders(session),
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("File deleted");
      refresh();
    } catch (err) {
      toast.error("Delete failed", { description: err?.message });
    }
  };

  const downloadUrl = (id) => `${BACKEND}/api/files/${id}/download`;

  return (
    <div data-testid={`${dataTestidPrefix}-attachments`} className={compact ? "" : "space-y-3"}>
      {!compact && (
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold tracking-[0.10em] uppercase text-[#6b7280] flex items-center gap-1.5">
            <Paperclip className="h-3 w-3" /> {label}
            {files.length > 0 && (
              <span className="text-[10px] text-[#94a3b8] font-semibold">· {files.length}</span>
            )}
          </div>
        </div>
      )}

      {showLabelInput && (
        <input
          type="text"
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
          placeholder="Optional label (e.g. 'Org chart')"
          className="w-full h-9 rounded-lg border border-[#e5e7eb] bg-white text-[12.5px] text-[#111827] px-3 placeholder:text-[#9ca3af] focus:border-[#C79A3B] outline-none"
          data-testid={`${dataTestidPrefix}-label-input`}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-[#94a3b8]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading attachments…
        </div>
      ) : (
        <>
          {files.length === 0 ? (
            <div className="text-[12px] text-[#9ca3af]">{emptyHint}</div>
          ) : (
            <div className={compact ? "flex flex-wrap gap-2" : "space-y-1.5"}>
              {files.map((f) => {
                const Icon = fileIcon(f.content_type);
                if (compact) {
                  return (
                    <a
                      key={f.id}
                      href={downloadUrl(f.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[11px] text-[#111827] max-w-[220px]"
                      data-testid={`${dataTestidPrefix}-pill-${f.id}`}
                    >
                      <Icon className="h-3 w-3 shrink-0 text-[#6b7280]" />
                      <span className="truncate">{f.label || f.filename}</span>
                    </a>
                  );
                }
                return (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-3 py-2"
                    data-testid={`${dataTestidPrefix}-row-${f.id}`}
                  >
                    <div className="h-8 w-8 shrink-0 rounded-md bg-[#f8fafc] flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#6b7280]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[#111827] truncate">
                        {f.label || f.filename}
                      </div>
                      <div className="text-[11px] text-[#94a3b8] truncate">
                        {f.filename} · {fmtBytes(f.size_bytes)} · {new Date(f.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </div>
                    </div>
                    <a
                      href={downloadUrl(f.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-8 w-8 shrink-0 rounded-md hover:bg-[#f3f4f6] flex items-center justify-center text-[#6b7280]"
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDelete(f.id)}
                      className="h-8 w-8 shrink-0 rounded-md hover:bg-[#fef2f2] flex items-center justify-center text-[#dc2626]"
                      title="Delete"
                      data-testid={`${dataTestidPrefix}-delete-${f.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-2">
            <label
              className={`inline-flex items-center gap-1.5 h-${compact ? "8" : "9"} px-3 rounded-lg border border-dashed border-[#d1d5db] text-[12px] font-semibold text-[#6b7280] hover:border-[#C79A3B] hover:text-[#C79A3B] hover:bg-[#fffbeb] cursor-pointer transition-colors`}
              data-testid={`${dataTestidPrefix}-upload-btn`}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {uploading ? "Uploading…" : (compact ? "Attach file" : "Upload file")}
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
            <span className="ml-2 text-[10.5px] text-[#9ca3af]">Max 25 MB</span>
          </div>
        </>
      )}
    </div>
  );
}
